import {AlpineComponent} from "alpinejs";
import {
  default as AjaxList,
  ListXDataContext as AjaxListXDataContext,
  ListOptions as AjaxListOptions
} from '../../../components/AjaxList/Classes/List';
import ListItem from "../../../components/AjaxList/Classes/Item";
import {IHistoryStateProps} from "../../../utils/historyState";

export type ListComponent = AlpineComponent<AjaxListXDataContext<ListItem> & {
  activeOrder: string;
  filterSetOrder: (event: Event) => void;
  filterItems: (order: string) => void;
  filterOption: (type: string, event: Event) => void;
  showFilterOptions: (option: string) => void;
  setFilterOptions: () => void;
  getSearchValue: () => void;
}>;

export type ListOptions = AjaxListOptions & IHistoryStateProps;

const List = (options: ListOptions, filterData = {}): ListComponent => {
  return {
    ...AjaxList(options, filterData),
    activeOrder: '',
    isLoading: false,

    created() {
      this.setFilterOptions();
    },

    // Set order
    filterSetOrder(event: Event) {
      this.isLoading = true;
      const eTarget = event.currentTarget as HTMLSelectElement;
      const value = eTarget.selectedOptions[0].value;
      const [order, field] = value.split(' ');

      if (field == 'TITLE') {
        this.filteredItems.sort((a, b) => {
          if (order === 'ASC') {
            return a.title.localeCompare(b.title);
          } else {
            return b.title.localeCompare(a.title);
          }
        });
      } else if (field == 'DATE') {
        this.filteredItems.sort((a, b) => {
          const dateA = a.seminardatestartfilter || '';
          const dateB = b.seminardatestartfilter || '';
          if (order === 'ASC') {
            return dateA.localeCompare(dateB);
          } else {
            return dateB.localeCompare(dateA);
          }
        });
      }

      this.filterItems(order);
      this.isLoading = false;
    },

    // Filter items
    filterItems($order = 'ASC') {
      this.isLoading = true;
      if ($order == 'ASC') {
        this.filteredItems = this.items;
      }

      if (this.filter && this.filter.data && Object.keys(this.filter.data).length > 0) {
        this.filteredItems = this.filteredItems.filter((item) => {
          // for each filter property
          for (const property in this.filter!.data) {
            let filterPropertyValue = this.filter!.data[property];
            let itemPropertyValue = item[property];

            // if filter data property is empty, skip it
            if ((!filterPropertyValue) ||
              (Array.isArray(filterPropertyValue) && filterPropertyValue.length === 0)
            ) {
              continue;
            }

            // Provider Bundesland
            if (property == 'provider_state') {
              if (!filterPropertyValue.includes(itemPropertyValue)) {
                return false;
              }
            }
            // Provider PLZ
            if (property == 'provider_postal_code') {
              if (!filterPropertyValue.includes(itemPropertyValue)) {
                return false;
              }
            }
            // Provider Ort
            if (property == 'provider_city') {
              if (!filterPropertyValue.includes(itemPropertyValue)) {
                return false;
              }
            }

            // Seminar Datum ab
            if (property == 'seminar_date_start') {
              if (itemPropertyValue) {
                const [day, month, year] = filterPropertyValue.split('.');
                const filterDate = new Date(`${year}-${month}-${day}`).getTime();

                const [itemDay, itemMonth, itemYear] = itemPropertyValue.split('.');
                const itemDate = new Date(`${itemYear}-${itemMonth}-${itemDay}`).getTime();

                if (itemDate < filterDate) {
                  return false;
                }
              }
            }
            // Seminar Datum bis
            if (property == 'seminar_date_end') {
              if (itemPropertyValue) {
                const [day, month, year] = filterPropertyValue.split('.');
                const filterDate = new Date(`${year}-${month}-${day}`).getTime();

                const [itemDay, itemMonth, itemYear] = itemPropertyValue.split('.');
                const itemDate = new Date(`${itemYear}-${itemMonth}-${itemDay}`).getTime();

                if (itemDate > filterDate) {
                  return false;
                }
              }
            }
            // Seminar Bundesland
            if (property == 'seminar_states') {
              if (!filterPropertyValue.includes(itemPropertyValue)) {
                return false;
              }
            }
            // Seminar PLZ
            if (property == 'seminar_postal_code') {
              if (!filterPropertyValue.includes(itemPropertyValue)) {
                return false;
              }
            }
            // Seminar Ort
            if (property == 'seminar_location') {
              if (!filterPropertyValue.includes(itemPropertyValue)) {
                return false;
              }
            }
            // Seminar Thema
            if (property == 'seminar_category') {
              if (!filterPropertyValue.includes(itemPropertyValue)) {
                return false;
              }
            }
            // Seminar Type
            if (property == 'seminar_type') {
              if (!filterPropertyValue.includes(itemPropertyValue)) {
                return false;
              }
            }
          }

          return true;
        });
      }

      if (this.pagination) {
        this.pagination.setTotalItems(this.filteredItems.length);
        this.pagination.goToPage(1);
      }

      this.isLoading = false;
    },

    // Filter input
    filterOption(type: string, event: Event) {
      const eTarget = event.target as HTMLInputElement;
      const value = eTarget.value.toString().toLowerCase();

      if (value.length >= 2) {
        // Filter results
        const filteredResults = this.filteredItems.filter(item =>
          item[type] && item[type].toString().toLowerCase().includes(value)
        );

        // Add results to ul list
        const ulParent = eTarget.parentElement;
        if (ulParent) {
          const list = ulParent.querySelector('ul') as HTMLUListElement;
          if (list) {
            list.innerHTML = ''; // Remove all existing li elements
            list.removeAttribute('hidden'); // Remove hidden attribute from ul

            const uniqueStates = new Set<string>();
            let isFirstItem = true;

            filteredResults.forEach(item => {
              const state = item[type];
              if (state && !uniqueStates.has(state)) {
                uniqueStates.add(state);

                const li = document.createElement('li');
                li.setAttribute('aria-selected', isFirstItem ? 'true' : 'false');
                isFirstItem = false;
                li.innerHTML = state.replace(new RegExp(value, 'gi'), (match: string) => `<mark>${match}</mark>`);
                li.addEventListener('click', () => {
                  eTarget.value = state;
                  list.setAttribute('hidden', 'hidden');
                  eTarget.focus();
                  // this.filter!.data[type] = state; // Filter items
                  // this.filterItems();
                });
                list.appendChild(li);
              }
            });
          }
        }
      }
    },

    // Show filter options
    showFilterOptions(option: string) {
      const filters = document.querySelectorAll('.wrapper--filters fieldset') as NodeListOf<HTMLElement>;
      const wrapperFilter = document.querySelector('.wrapper--filter') as HTMLElement;
      if (filters && wrapperFilter) {
        filters.forEach((filter) => {
          const filterType = filter.dataset.filter;

          if (filterType) {
            if (option == 'all' || option == 'post') {
              wrapperFilter.classList.remove('show');
              filter.classList.remove('show');
            } else {
              if (filterType.includes(option)) {
                wrapperFilter.classList.add('show');
                filter.classList.add('show');
              } else {
                filter.classList.remove('show');
              }
            }

            // Sort filter
            const orderFilters = document.querySelectorAll('.wrapper--order-by fieldset.order-by') as NodeListOf<HTMLElement>;
            if (orderFilters) {
              orderFilters.forEach((filter) => {
                filter.classList.remove('show');
                const type = filter.dataset.showForType;
                if (option == 'seminar') {
                  if (type == 'seminar') {
                    filter.classList.add('show');
                  }
                } else {
                  if (type == 'post') {
                    filter.classList.add('show');
                  }
                }
              });
            }
          }
        });
      }
    },

    setFilterOptions() {
      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);
      const option = urlParams.get('s--type');
      const wrapperTypes = document.querySelector('.wrapper--types') as HTMLElement;

      if (wrapperTypes && option) {
        const radios = wrapperTypes.querySelectorAll('input[type="radio"]') as NodeListOf<HTMLInputElement>;
        if (radios) {
          radios.forEach((radio) => {
            if (radio.value.includes(option)) {
              radio.checked = true;
            }
          });
        }

        this.showFilterOptions(option);

        // Remove obsolete filters
        if (option == 'seminar') {
          const url = new URL(window.location.href);
          url.searchParams.delete("provider_state");
          url.searchParams.delete("provider_postal_code");
          url.searchParams.delete("provider_city");
          window.history.replaceState({}, '', url.toString());
        } else if (option == 'provider') {
          const url = new URL(window.location.href);
          url.searchParams.delete("seminar_date_start");
          url.searchParams.delete("seminar_date_end");
          url.searchParams.delete("seminar_states");
          url.searchParams.delete("seminar_postal_code");
          url.searchParams.delete("seminar_location");
          url.searchParams.delete("seminar_category");
          url.searchParams.delete("seminar_type");
          window.history.replaceState({}, '', url.toString());
        } else {
          const url = new URL(window.location.href);
          url.searchParams.delete("provider_state");
          url.searchParams.delete("provider_postal_code");
          url.searchParams.delete("provider_city");
          url.searchParams.delete("seminar_date_start");
          url.searchParams.delete("seminar_date_end");
          url.searchParams.delete("seminar_states");
          url.searchParams.delete("seminar_postal_code");
          url.searchParams.delete("seminar_location");
          url.searchParams.delete("seminar_category");
          url.searchParams.delete("seminar_type");
          window.history.replaceState({}, '', url.toString());
        }
      }
    },

    getSearchValue() {
      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);

      return urlParams.get('search') ? urlParams.get('search') : null;
    }
  }
}

export default List;
